/**
 * AddFoodDialog — search → pick food → set serving + qty → add to a meal/feed group.
 *
 * Shared by Diet Recall and Meal Plan. The `isMealPlan` flag is forwarded to
 * the mutation hooks (`useAddMealEntry`, `useUpdateMealEntry`) which choose
 * the correct backend endpoint (diet-recall-entry vs meal-plan-entry).
 *
 * Tube feeding has a "smart merge" path: if the open feed group has a
 * comment-only entry, the new food is PATCHED onto it instead of POSTing a
 * new entry. The list of `feedEntries` enables that lookup.
 */
import * as React from "react";

import {
  Widget,
  WidgetContent,
  WidgetHeader,
} from "@/components/consultation/shared/Widget";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import {
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import {
  useAddMealEntry,
  useFoodSearch,
  useFoodServings,
  useFoodTypes,
  useUpdateMealEntry,
} from "@/hooks/useNutritionEvaluation";
import type { FeedEntry } from "@/types/nutrition";
import { Apple, Loader2 } from "lucide-react";

import { quantityPatternFor } from "./meal-constants";
import { FoodNutrientPieChart } from "./nutrition-charts";
import { NUTRITION_COLORS } from "./nutrition-chart-config";

interface AddFoodDialogProps {
  open: boolean;
  onClose: () => void;
  mealGroupId: string;
  mealTimeLabel: string;
  appointmentId: string;
  isTubeFeeding: boolean;
  isMealPlan?: boolean;
  planIndex?: number;
  /** For tube-feeding smart merge with an existing comment-only entry. */
  feedEntries?: FeedEntry[];
  /** Lets the user re-target a different food group from inside the dialog. */
  mealGroupOptions?: Array<{ id: string; label: string }>;
}

export const AddFoodDialog: React.FC<AddFoodDialogProps> = ({
  open,
  onClose,
  mealGroupId,
  mealTimeLabel,
  appointmentId,
  isTubeFeeding,
  isMealPlan = false,
  feedEntries = [],
  mealGroupOptions,
}) => {
  const [selectedMealGroupId, setSelectedMealGroupId] =
    React.useState(mealGroupId);
  React.useEffect(() => {
    if (open) setSelectedMealGroupId(mealGroupId);
  }, [open, mealGroupId]);
  const [searchQuery, setSearchQuery] = React.useState("");
  const [selectedFood, setSelectedFood] = React.useState<{
    id: number;
    name: string;
    energy_kcal: string;
    food_item_nutrient_values?: {
      energy: number;
      protein: number;
      carbs: number;
      fat: number;
      fiber: number;
    };
  } | null>(null);
  const [selectedServing, setSelectedServing] = React.useState<string>("");
  const [quantity, setQuantity] = React.useState("1");
  const [selectedFoodType, setSelectedFoodType] = React.useState("all");
  const [sourceFilter, setSourceFilter] = React.useState("all");

  const { data: foodTypes = [] } = useFoodTypes();
  const foodTypeTabs = React.useMemo(
    () => [{ value: "all", label: "All" }, ...foodTypes],
    [foodTypes]
  );

  const handleFoodTypeChange = React.useCallback((value: string) => {
    setSelectedFoodType(value);
    // Clear any in-flight selection so a hidden item doesn't stay "selected".
    setSelectedFood(null);
    setSelectedServing("");
  }, []);

  const { data: searchResults, isLoading: isSearching } = useFoodSearch(
    searchQuery,
    appointmentId,
    searchQuery.length >= 2,
    selectedFoodType === "all" ? undefined : selectedFoodType
  );

  const sourceChips = React.useMemo(() => {
    const sources = new Set<string>();
    for (const food of searchResults?.results ?? []) {
      const s = food.source?.trim();
      if (s) sources.add(s);
    }
    return Array.from(sources).sort((a, b) => a.localeCompare(b));
  }, [searchResults]);

  const filteredResults = React.useMemo(() => {
    const all = searchResults?.results ?? [];
    if (sourceFilter === "all") return all;
    return all.filter((f) => f.source?.trim() === sourceFilter);
  }, [searchResults, sourceFilter]);

  React.useEffect(() => {
    if (sourceFilter !== "all" && !sourceChips.includes(sourceFilter)) {
      setSourceFilter("all");
    }
  }, [sourceChips, sourceFilter]);

  const { data: servings = [] } = useFoodServings(
    selectedFood?.id || 0,
    !!selectedFood
  );

  const addEntry = useAddMealEntry(appointmentId, isTubeFeeding, isMealPlan);
  const updateEntry = useUpdateMealEntry(
    appointmentId,
    isTubeFeeding,
    isMealPlan
  );

  // Smart merge: find a comment-only entry in this feed group.
  const findCommentOnlyEntry = React.useCallback(() => {
    if (!isTubeFeeding) return null;
    return feedEntries.find(
      (entry) =>
        !entry.food_item_details &&
        !entry.feed_item_details &&
        entry.comment &&
        entry.comment.trim() !== ""
    );
  }, [isTubeFeeding, feedEntries]);

  const getAdjustedNutrients = React.useCallback(() => {
    if (!selectedFood?.food_item_nutrient_values) return null;
    const serving = servings.find((s) => s.id.toString() === selectedServing);
    const multiplier = serving ? parseFloat(serving.multiplier) : 1;
    const qty = parseFloat(quantity) || 0;
    const totalMultiplier = qty * multiplier;

    const nutrients = selectedFood.food_item_nutrient_values;
    return {
      energy: nutrients.energy * totalMultiplier,
      protein: nutrients.protein * totalMultiplier,
      carbs: nutrients.carbs * totalMultiplier,
      fat: nutrients.fat * totalMultiplier,
      fiber: nutrients.fiber * totalMultiplier,
    };
  }, [selectedFood, selectedServing, quantity, servings]);

  const adjustedNutrients = getAdjustedNutrients();

  const resetForm = () => {
    setSearchQuery("");
    setSelectedFood(null);
    setSelectedServing("");
    setQuantity("1");
  };

  // Whole servings only when the food contains something indivisible — a
  // capsule or a strip. Keyed on the food's flag, never on "is a supplement":
  // a whey scoop halves perfectly well.
  const wholeServingsOnly = Boolean(
    (selectedFood as { requires_whole_servings?: boolean } | null)
      ?.requires_whole_servings
  );

  const handleQuantityChange = (value: string) => {
    if (!quantityPatternFor(wholeServingsOnly).test(value)) return;
    setQuantity(value);
  };

  const handleAdd = () => {
    if (!selectedFood || !selectedServing) return;

    if (isTubeFeeding) {
      const commentOnlyEntry = findCommentOnlyEntry();
      if (commentOnlyEntry) {
        updateEntry.mutate(
          {
            entryId: commentOnlyEntry.id,
            data: {
              food_item: selectedFood.id,
              serving_size: parseInt(selectedServing),
              quantity: parseFloat(quantity),
            },
          },
          {
            onSuccess: () => {
              onClose();
              resetForm();
            },
          }
        );
        return;
      }

      addEntry.mutate({
        feed_group: selectedMealGroupId,
        food_item: selectedFood.id,
        serving_size: parseInt(selectedServing),
        quantity,
      } as Parameters<typeof addEntry.mutate>[0]);
    } else {
      addEntry.mutate({
        meal_group: selectedMealGroupId,
        food_item: selectedFood.id,
        serving_size: parseInt(selectedServing),
        quantity,
      } as Parameters<typeof addEntry.mutate>[0]);
    }
    onClose();
    resetForm();
  };

  return (
    <Dialog
      open={open}
      onOpenChange={() => {
        onClose();
        resetForm();
      }}
    >
      <DialogContent className="sm:max-w-[900px] max-h-[95vh] flex flex-col p-0 gap-0 overflow-hidden border-border/60">
        <DialogHeader className="p-6 pb-0 shrink-0">
          <DialogTitle className="flex items-center gap-2 z-h2 text-foreground">
            <Apple className="h-5 w-5 text-primary" />
            Add Food Item
          </DialogTitle>
        </DialogHeader>
        <Separator className="mt-4" />

        <div className="min-h-0 flex-1 overflow-y-auto p-6 bg-background flex flex-col gap-4">
          <Input
            placeholder="Start typing food item..."
            value={searchQuery}
            onChange={(e) => {
              setSearchQuery(e.target.value);
              setSelectedFood(null);
            }}
            className="w-full"
          />

          <p className="z-caption text-muted-foreground -mt-2">
            {isSearching
              ? "Searching…"
              : searchQuery.length < 2
                ? "Type at least 2 characters to search"
                : `${filteredResults.length} items found`}
          </p>

          <Tabs
            value={selectedFoodType}
            onValueChange={handleFoodTypeChange}
            className="w-full"
          >
            <TabsList className="w-full">
              {foodTypeTabs.map((tab) => (
                <TabsTrigger key={tab.value} value={tab.value}>
                  {tab.label}
                </TabsTrigger>
              ))}
            </TabsList>
          </Tabs>

          {sourceChips.length > 0 && (
            <ToggleGroup
              type="single"
              spacing={2}
              value={sourceFilter}
              onValueChange={(v) => v && setSourceFilter(v)}
              variant="outline"
              size="sm"
              className="flex-wrap"
            >
              <ToggleGroupItem value="all">All</ToggleGroupItem>
              {sourceChips.map((source) => (
                <ToggleGroupItem key={source} value={source}>
                  {source}
                </ToggleGroupItem>
              ))}
            </ToggleGroup>
          )}

          <Widget className="overflow-hidden">
            <div className="h-64 overflow-y-auto">
              {isSearching && (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="h-6 w-6 animate-spin" />
                </div>
              )}

              {filteredResults.length > 0 && (
                <table className="w-full caption-bottom text-sm">
                  <TableHeader>
                    <TableRow className="hover:bg-transparent">
                      <TableHead className="px-4 text-muted-foreground/80 sticky top-0 z-10 bg-muted">
                        Food Name
                      </TableHead>
                      <TableHead className="px-4 w-40 text-muted-foreground/80 sticky top-0 z-10 bg-muted">
                        Source
                      </TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredResults.map((food) => (
                      <TableRow
                        key={food.id}
                        data-state={
                          selectedFood?.id === food.id ? "selected" : undefined
                        }
                        className="cursor-pointer data-[state=selected]:bg-primary/10 hover:bg-muted/40"
                        onClick={() => {
                          setSelectedFood(food);
                          setSelectedServing("");
                        }}
                      >
                        <TableCell className="px-4 z-body-md text-foreground">
                          {food.name}
                        </TableCell>
                        <TableCell className="px-4 text-muted-foreground">
                          {food.source && food.source.trim() ? (
                            <Badge variant="secondary">{food.source}</Badge>
                          ) : (
                            "—"
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </table>
              )}

              {searchQuery.length >= 2 &&
                !isSearching &&
                filteredResults.length === 0 && (
                  <div className="text-center py-8 text-muted-foreground">
                    No food items found
                  </div>
                )}

              {searchQuery.length < 2 && (
                <div className="text-center py-8 text-muted-foreground">
                  Type at least 2 characters to search
                </div>
              )}
            </div>
          </Widget>

          {selectedFood && (
            <Widget>
              <WidgetHeader
                title={selectedFood.name}
                className="justify-center bg-muted/40 border-b border-border/60 py-2"
              />
              <WidgetContent className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-3">
                <Widget>
                  <WidgetContent className="py-4 space-y-3">
                    <div className="flex items-center gap-3">
                      <label className="z-h3 text-foreground w-28 shrink-0">
                        Serving Size
                      </label>
                      <Select
                        value={selectedServing}
                        onValueChange={setSelectedServing}
                        disabled={servings.length === 0}
                      >
                        <SelectTrigger className="flex-1 min-w-0">
                          <SelectValue placeholder="Select" />
                        </SelectTrigger>
                        <SelectContent>
                          {servings.map((serving) => (
                            <SelectItem
                              key={serving.id}
                              value={serving.id.toString()}
                            >
                              {serving.description || serving.serving_unit.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="flex items-center gap-3">
                      <label className="z-h3 text-foreground w-28 shrink-0">
                        Quantity
                      </label>
                      <Input
                        type="text"
                        inputMode="decimal"
                        aria-label="Quantity"
                        placeholder="-"
                        value={quantity}
                        onChange={(e) => handleQuantityChange(e.target.value)}
                        disabled={!selectedServing}
                        className="w-20 text-center"
                      />
                    </div>

                    <div className="flex items-center gap-3">
                      <label className="z-h3 text-foreground w-28 shrink-0">
                        Food Group
                      </label>
                      {mealGroupOptions && mealGroupOptions.length > 1 ? (
                        <Select
                          value={selectedMealGroupId}
                          onValueChange={setSelectedMealGroupId}
                        >
                          <SelectTrigger className="flex-1">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {mealGroupOptions.map((opt) => (
                              <SelectItem key={opt.id} value={opt.id}>
                                {opt.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      ) : (
                        <Badge variant="secondary" className="z-body">
                          {mealGroupOptions?.find(
                            (opt) => opt.id === selectedMealGroupId
                          )?.label || mealTimeLabel}
                        </Badge>
                      )}
                    </div>
                  </WidgetContent>
                </Widget>

                <Widget>
                  <WidgetContent className="py-4 h-full flex items-center justify-center">
                    {adjustedNutrients ? (
                      <div className="flex items-center justify-center gap-4">
                        <FoodNutrientPieChart
                          energy={adjustedNutrients.energy}
                          protein={adjustedNutrients.protein}
                          carbs={adjustedNutrients.carbs}
                          fat={adjustedNutrients.fat}
                          fiber={adjustedNutrients.fiber}
                        />
                        <div className="flex flex-col gap-1 z-body">
                          {(
                            [
                              { key: "protein", label: "Protein", k: 4 },
                              { key: "carbs", label: "Carbs", k: 4 },
                              { key: "fat", label: "Fat", k: 9 },
                              { key: "fiber", label: "Fiber", k: 2 },
                            ] as const
                          ).map((m) => (
                            <div
                              key={m.key}
                              className="flex items-center gap-2"
                            >
                              <div
                                className="w-3 h-3 rounded-full"
                                style={{
                                  backgroundColor:
                                    NUTRITION_COLORS[
                                      m.key === "fat" ? "fats" : m.key
                                    ],
                                }}
                              />
                              <span>
                                {m.label}: {adjustedNutrients[m.key].toFixed(1)}
                                g
                              </span>
                              <span className="text-muted-foreground">
                                (
                                {adjustedNutrients.energy > 0
                                  ? (
                                      ((adjustedNutrients[m.key] * m.k) /
                                        adjustedNutrients.energy) *
                                      100
                                    ).toFixed(1)
                                  : 0}
                                %)
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>
                    ) : (
                      <div className="text-center text-muted-foreground z-body py-6">
                        Select a serving size to see nutrients
                      </div>
                    )}
                  </WidgetContent>
                </Widget>
              </WidgetContent>
            </Widget>
          )}
        </div>

        <DialogFooter className="px-6 py-4 mt-auto border-t border-border/60 bg-background sm:justify-center">
          <Button
            onClick={handleAdd}
            disabled={
              !selectedFood ||
              !selectedServing ||
              !quantity ||
              parseFloat(quantity) <= 0 ||
              !selectedMealGroupId ||
              addEntry.isPending ||
              updateEntry.isPending
            }
          >
            {(addEntry.isPending || updateEntry.isPending) && (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            )}
            Add Item
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
